const CACHE_KEY = 'resolvedServer';
const LOOKUP_TIMEOUT_MS = 5000;

interface ResolvableEnv {
  apiUrl: string;
  host: string;
  domain: string;
}

/** Client host as stored in instance_config.cst_url: <client>-config.serinaplus.com -> <client>.serinaplus.com */
function clientHost(env: ResolvableEnv): string {
  if (location.host.includes('localhost')) {
    return env.domain;
  }
  const suffix = '-config.serinaplus.com';
  return location.host.endsWith(suffix)
    ? `${location.host.slice(0, -suffix.length)}.serinaplus.com`
    : location.host;
}

function apply(serverurl: string, env: ResolvableEnv): boolean {
  try {
    const url = new URL(serverurl);
    env.apiUrl = `${url.protocol}//${url.host}`;
    // <label>.serinaplus.com mirrors the label of the cloudapp.azure.com server host
    env.host = `${url.hostname.split('.')[0]}.serinaplus.com`;
    return true;
  } catch {
    return false;
  }
}

/**
 * Looks up the backend for the current client host from instance_config.serverurl
 * (via the bootstrap server already in env.apiUrl) and points the environment at it.
 * On any failure the bootstrap server is kept.
 */
export async function resolveServer(env: ResolvableEnv): Promise<void> {
  const host = clientHost(env);

  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (cached?.clientHost === host && cached?.serverurl && apply(cached.serverurl, env)) {
      return;
    }
  } catch {
    // ignore unreadable cache
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const res = await fetch(`${env.apiUrl}/apiv1.1/Instance/resolveServer`, {
      headers: {
        'X-API-Key': 'bI7_OesAIGPrBIBxvYbiaxfGzRUAmRdcdMvigsmG6gh6AzFutEqx_Q==',
        domain: host,
      },
      signal: controller.signal,
    });
    if (!res.ok) {
      return;
    }
    const { serverurl } = await res.json();
    if (serverurl && apply(serverurl, env)) {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ clientHost: host, serverurl }));
      } catch {
        // storage unavailable
      }
    }
  } catch (err) {
    console.warn('resolveServer failed, using bootstrap server', err);
  } finally {
    clearTimeout(timer);
  }
}
